import React, { useEffect, useState } from 'react';
import { ListTree } from 'lucide-react';

/**
 * Sticky Table-of-Contents sidebar for long legal/marketing pages.
 *
 * Scans the provided container ref for h2 (and optional h3) elements,
 * auto-generates anchor IDs, and renders a vertical nav with smooth scrolling
 * + an active-section highlight driven by an IntersectionObserver.
 */
export default function TableOfContents({ contentRef, includeH3 = false, title = 'On this page' }) {
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Build the heading list from the rendered DOM
  useEffect(() => {
    if (!contentRef?.current) return;

    const slugify = (text) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

    const selector = includeH3 ? 'h2, h3' : 'h2';
    const headings = Array.from(contentRef.current.querySelectorAll(selector));
    const used = new Set();

    const list = headings.map((el) => {
      let id = el.id;
      if (!id) {
        const base = slugify(el.textContent || '');
        let candidate = base || `section-${Math.random().toString(36).slice(2, 7)}`;
        let i = 1;
        while (used.has(candidate)) {
          candidate = `${base}-${i++}`;
        }
        id = candidate;
        el.id = id;
      }
      used.add(id);
      return { id, text: el.textContent || '', level: el.tagName.toLowerCase() };
    });

    setSections(list);
  }, [contentRef, includeH3]);

  // Track which section is currently most visible
  useEffect(() => {
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // pick the topmost visible entry
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 96; // header height
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveId(id);
  };

  if (sections.length === 0) return null;

  return (
    <aside
      className="hidden lg:block w-64 flex-shrink-0"
      data-testid="policy-toc"
    >
      <div className="sticky top-24">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">
            <ListTree className="w-3.5 h-3.5" />
            <span>{title}</span>
          </div>
          <nav className="space-y-1">
            {sections.map((s) => {
              const isActive = activeId === s.id;
              const indent = s.level === 'h3' ? 'pl-5' : 'pl-2';
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={(e) => handleClick(e, s.id)}
                  data-testid={`toc-item-${s.id}`}
                  className={`block ${indent} pr-2 py-1.5 text-sm rounded-md border-l-2 transition-colors ${
                    isActive
                      ? 'border-[#C85A42] text-[#C85A42] font-medium bg-[#C85A42]/5'
                      : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {s.text}
                </a>
              );
            })}
          </nav>
        </div>
      </div>
    </aside>
  );
}
